import { getImageFromIndexedDB } from '@/utils/imageStorage';
import { resolveImageUrlToBlob } from '@/utils/imageUrlUtils';
import { pickImageSignOptions } from '@/utils/imageDisplayAccess';
import {
  addDownloadCenterRecord,
  collectKnownImageBytes,
  enqueueDownloadCenterJob,
  estimateClientZipBytes,
  isDownloadTaskCancelled,
  queueBrowserZipDownload,
  registerDownloadTaskAbort,
  shouldUseServerZipDownload,
  submitServerDownloadTask,
  updateDownloadCenterProgress,
} from '@/utils/downloadCenter';
import { resolveCanonicalManagedUrlForExport } from '@/utils/exportPublicImageUrl';

function sanitizeDownloadBaseName(name, fallback = 'image') {
  return String(name || fallback).replace(/[\\/:*?"<>|]+/g, '_').trim() || fallback;
}

function extFromDownloadBlob(blob, imageUrl) {
  if (blob?.type === 'image/png') return 'png';
  if (blob?.type === 'image/webp') return 'webp';
  if (blob?.type === 'image/gif') return 'gif';
  const fromPath = String(imageUrl || '').split('?')[0].match(/\.([a-zA-Z0-9]+)$/);
  return fromPath?.[1]?.toLowerCase() || 'jpg';
}

function buildDownloadFileName(image, imageUrl, blob) {
  let fileName = String(image.name || image.id || 'image').trim() || 'image';
  if (!/\.[a-zA-Z0-9]+$/.test(fileName)) {
    fileName = `${fileName}.${extFromDownloadBlob(blob, imageUrl)}`;
  }
  return fileName;
}

function trimUrl(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/** 会话 blob: 刷新即失效，下载不可当作稳定源 */
function isEphemeralBlobUrl(url) {
  return trimUrl(url).startsWith('blob:');
}

/** 可跨刷新用于下载的地址：受管 /uploads、https、data:；排除 blob: / indexeddb:// */
function isDurableDownloadUrl(url) {
  const s = trimUrl(url);
  if (!s) return false;
  if (isEphemeralBlobUrl(s)) return false;
  if (s.startsWith('indexeddb://')) return false;
  return true;
}

function pickFirstDurable(candidates) {
  for (const value of candidates) {
    const s = trimUrl(value);
    if (isDurableDownloadUrl(s)) return s;
  }
  return '';
}

function pickFirstEphemeralBlob(candidates) {
  for (const value of candidates) {
    const s = trimUrl(value);
    if (isEphemeralBlobUrl(s)) return s;
  }
  return '';
}

/**
 * 解析图片空间下载用原始地址。
 * 优先级：持久化 url → IndexedDB(serverUrl 优先) → data: → 当前会话 blob:（最后兜底）。
 * 切勿在 IndexedDB 之前采纳 dataUrl/src 里的会话 blob:，否则刷新后下载失败。
 */
export async function resolveImageSpaceRawUrl(image) {
  if (!image || typeof image !== 'object') return '';

  // 1) 记录上的持久化服务端地址（跳过误写进 url/src 的 blob:）
  let imageUrl = pickFirstDurable([
    image.url,
    image.storedUrl,
    image.serverUrl,
    image.imageUrl,
    image.src,
  ]);

  // 2) IndexedDB：内部已优先 serverUrl，再 dataURL；须排在「记录上的 blob dataUrl」之前
  if (!imageUrl && image.id != null && String(image.id).trim()) {
    try {
      const fromIdb = await getImageFromIndexedDB(image.id);
      const idbUrl = trimUrl(fromIdb);
      if (isDurableDownloadUrl(idbUrl) || isEphemeralBlobUrl(idbUrl)) {
        // IDB 现读出的 blob: 是本会话新建的 Object URL（底层有 Blob），可用
        imageUrl = idbUrl;
      }
    } catch (error) {
      console.warn('[图片空间下载] IndexedDB 读取失败', image.id, error);
    }
  }

  // 3) 真正的 data:（跨会话仍在内存/记录里时可用）；blob 伪装在 dataUrl 字段则跳过
  if (!imageUrl) {
    const dataUrl = trimUrl(image.dataUrl);
    if (dataUrl.startsWith('data:')) imageUrl = dataUrl;
  }

  // 4) 最后才用会话 blob:（同页未刷新时尚可 fetch）
  if (!imageUrl) {
    imageUrl = pickFirstEphemeralBlob([
      image.src,
      image.dataUrl,
      image.url,
      image.imageUrl,
    ]);
  }

  return trimUrl(imageUrl);
}

/**
 * 下载图片空间单张图片。
 * @param {Record<string, unknown>} image
 * @param {{ onError?: (message: string) => void }} [options]
 */
export async function downloadImageSpaceImage(image, { onError } = {}) {
  try {
    const imageUrl = await resolveImageSpaceRawUrl(image);

    if (!imageUrl) {
      onError?.('无法获取图片地址');
      return;
    }

    const blob = await resolveImageUrlToBlob(imageUrl, pickImageSignOptions(image));
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = buildDownloadFileName(image, imageUrl, blob);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  } catch (error) {
    console.error('下载图片失败:', error);
    onError?.(error?.message || '下载失败，请重试');
  }
}

/**
 * 批量下载图片空间图片为 ZIP（写入下载中心）。
 * @returns {Promise<{ downloaded: number, total: number, queued?: boolean } | null>}
 */
export async function downloadImageSpaceImagesZip(images, { onError, zipFileName, source = '图片空间' } = {}) {
  const list = Array.isArray(images) ? images.filter(Boolean) : [];
  if (list.length === 0) {
    onError?.('请先选择图片');
    return null;
  }

  const resolvedItems = [];
  for (let i = 0; i < list.length; i += 1) {
    const image = list[i];
    const rawUrl = await resolveImageSpaceRawUrl(image);
    if (!rawUrl) continue;
    resolvedItems.push({
      image,
      rawUrl,
      baseName: sanitizeDownloadBaseName(image.name || image.id || `image_${i + 1}`, `image_${i + 1}`),
      signOptions: pickImageSignOptions(image),
    });
  }

  if (resolvedItems.length === 0) {
    onError?.('无法获取图片地址');
    return null;
  }

  try {
    const stamp = new Date().toISOString().slice(0, 10);
    const safeBatch = String(zipFileName || '').trim()
      || `图片空间_${stamp}_${list.length}张.zip`;
    const fileName = safeBatch.endsWith('.zip') ? safeBatch : `${safeBatch}.zip`;
    const recordId = `dl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    registerDownloadTaskAbort(recordId, new AbortController());

    const writtenPaths = new Set();
    const serverItems = [];
    for (let i = 0; i < resolvedItems.length; i += 1) {
      const item = resolvedItems[i];
      const extGuess = String(item.rawUrl || '').split('?')[0].match(/\.([a-zA-Z0-9]+)$/)?.[1]?.toLowerCase() || 'jpg';
      let stem = item.baseName.replace(/\.[a-zA-Z0-9]+$/, '');
      let path = `${stem}.${extGuess}`;
      let suffix = 1;
      while (writtenPaths.has(path)) {
        path = `${stem}_${suffix}.${extGuess}`;
        suffix += 1;
      }
      writtenPaths.add(path);
      const shortUrl = resolveCanonicalManagedUrlForExport(item.rawUrl) || item.rawUrl;
      serverItems.push({ url: shortUrl, path });
    }

    addDownloadCenterRecord({
      id: recordId,
      fileName,
      url: '',
      source,
      status: 'downloading',
      createdAt: new Date().toISOString(),
      completedAt: null,
      downloadedAt: null,
      error: null,
      itemCount: list.length,
      progress: 0,
    });

    const imageKnownBytes = collectKnownImageBytes(resolvedItems.map((row) => row.image));
    if (shouldUseServerZipDownload({
      itemCount: serverItems.length,
      estimatedBytes: estimateClientZipBytes({
        itemCount: serverItems.length,
        knownBytes: imageKnownBytes,
      }),
    })) {
      await submitServerDownloadTask({
        fileName,
        source,
        items: serverItems,
        recordId,
        required: true,
      });
      return { downloaded: serverItems.length, total: list.length, queued: true, serverSide: true };
    }

    return enqueueDownloadCenterJob(async () => {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      const clientWritten = new Set();

      for (let i = 0; i < resolvedItems.length; i += 1) {
        if (isDownloadTaskCancelled(recordId)) break;
        const item = resolvedItems[i];
        try {
          const blob = await resolveImageUrlToBlob(item.rawUrl, item.signOptions || undefined);
          const ext = extFromDownloadBlob(blob, item.rawUrl);
          let stem = item.baseName.replace(/\.[a-zA-Z0-9]+$/, '');
          let path = `${stem}.${ext}`;
          let suffix = 1;
          while (clientWritten.has(path)) {
            path = `${stem}_${suffix}.${ext}`;
            suffix += 1;
          }
          clientWritten.add(path);
          zip.file(path, blob);
          updateDownloadCenterProgress(recordId, Math.round(((i + 1) / resolvedItems.length) * 80));
        } catch (error) {
          console.warn('[图片空间批量下载] 单张失败', item.image?.id, error);
        }
      }

      if (clientWritten.size === 0) {
        throw new Error('FETCH_ALL_FAILED');
      }
      if (isDownloadTaskCancelled(recordId)) {
        return { downloaded: clientWritten.size, total: list.length, queued: true };
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      await queueBrowserZipDownload({
        blob: zipBlob,
        fileName,
        source,
        recordId,
        meta: { itemCount: clientWritten.size },
      });

      return { downloaded: clientWritten.size, total: list.length, queued: true };
    });
  } catch (error) {
    console.error('[图片空间批量下载] 失败:', error);
    onError?.(error?.message || '下载失败，请重试');
    return null;
  }
}
